// The referral (affiliate) program, laid out (design language F). Facts come
// from the program's terms (/affiliate/terms) and its Tolt setup: 20% of
// every payment a referred customer makes for their first 3 months, the
// referral counts if they buy within 30 days of clicking, paid monthly in
// USD, 13+, no paid search ads or spam. Sign-up and the dashboard live at
// affiliate.jammable.com (Tolt). Prices in the examples are the USD plan
// prices from utils/pricing, so they follow any price change.
import React, { useRef, useState } from "react";
import { GetServerSideProps } from "next";
import Link from "next/link";
import { NextSeo } from "next-seo";
import { useQuery } from "react-query";
import { Check, Mic, Share2, UserPlus, Wallet, X } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@jammable/ui-components/Accordion";
import connectToDb from "@/utils/mongodb";
import { formatPriceWithLocale, getPrice } from "@/utils/pricing";

const JOIN_URL = "https://affiliate.jammable.com";
const RATE = 0.2;
const MONTHS = 3;

// What each plan's first three payments are (first month promo, then the
// standing price), and so what one referral earns over them.
const PLANS = [
  {
    key: "basic",
    label: "Basic",
    first: getPrice("USD", "basic") ?? 0,
    then: getPrice("USD", "basic-discount") ?? 0,
  },
  {
    key: "creator",
    label: "Creator",
    first: getPrice("USD", "creator") ?? 0,
    then: getPrice("USD", "creator-discount") ?? 0,
  },
  {
    key: "power",
    label: "Power",
    first: getPrice("USD", "power") ?? 0,
    then: getPrice("USD", "power") ?? 0,
  },
] as const;
type PlanKey = (typeof PLANS)[number]["key"];

const payments = (p: (typeof PLANS)[number]) =>
  [p.first, ...Array(MONTHS - 1).fill(p.then)] as number[];
const perReferral = (p: (typeof PLANS)[number]) =>
  payments(p).reduce((n, x) => n + x * RATE, 0);
const usd = (n: number) => formatPriceWithLocale(n, "USD");
const usdRound = (n: number) => `$${Math.round(n).toLocaleString("en-US")}`;

function Stat({ big, small }: { big: string; small: string }) {
  return (
    <div className="tw-flex tw-flex-col tw-gap-1 tw-rounded-[26px] tw-bg-jam-lav tw-p-5">
      <span className="jam-display tw-text-[34px] tw-font-extrabold tw-leading-none tw-tracking-[-0.04em] md:tw-text-[40px]">
        {big}
      </span>
      <span className="tw-text-[13px] tw-font-bold tw-text-jam-muted">
        {small}
      </span>
    </div>
  );
}

// The calculator's slider, in the studio's style (no native range input).
function Slider({
  min,
  max,
  value,
  onChange,
  label,
}: {
  min: number;
  max: number;
  value: number;
  onChange: (v: number) => void;
  label: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [held, setHeld] = useState(false);
  const at = (value - min) / (max - min);
  const fromX = (x: number) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const f = Math.min(
      1,
      Math.max(0, (x - r.left - 13) / Math.max(1, r.width - 26)),
    );
    onChange(Math.round(min + f * (max - min)));
  };
  return (
    <div
      ref={ref}
      role="slider"
      tabIndex={0}
      aria-label={label}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={value}
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        setHeld(true);
        fromX(e.clientX);
      }}
      onPointerMove={(e) => held && fromX(e.clientX)}
      onPointerUp={() => setHeld(false)}
      onKeyDown={(e) => {
        const step = e.shiftKey ? 10 : 1;
        if (e.key === "ArrowRight" || e.key === "ArrowUp")
          onChange(Math.min(max, value + step));
        else if (e.key === "ArrowLeft" || e.key === "ArrowDown")
          onChange(Math.max(min, value - step));
        else return;
        e.preventDefault();
      }}
      className="tw-relative tw-flex tw-h-10 tw-w-full tw-cursor-pointer tw-touch-none tw-select-none tw-items-center tw-outline-none focus-visible:tw-ring-[3px] focus-visible:tw-ring-jam-purple/40 tw-rounded-full"
    >
      <div className="tw-relative tw-h-3 tw-w-full tw-overflow-hidden tw-rounded-full tw-bg-white">
        <div
          className="tw-absolute tw-inset-y-0 tw-left-0 tw-rounded-full tw-bg-gradient-to-r tw-from-jam-deep tw-to-jam-purple"
          style={{ width: `calc(13px + (100% - 26px) * ${at})` }}
        />
      </div>
      <span
        className={`tw-pointer-events-none tw-absolute tw-top-1/2 tw-h-[26px] tw-w-[26px] -tw-translate-x-1/2 -tw-translate-y-1/2 tw-rounded-full tw-border-4 tw-border-solid tw-border-jam-purple tw-bg-white tw-shadow-[0_2px_8px_rgba(46,15,82,0.25)] tw-transition-transform ${held ? "tw-scale-110" : ""}`}
        style={{ left: `calc(13px + (100% - 26px) * ${at})` }}
      />
    </div>
  );
}

export default function EarnPage({ voices }: { voices: number }) {
  const [referrals, setReferrals] = useState(20);
  const [plan, setPlan] = useState<PlanKey>("creator");
  const chosen = PLANS.find((p) => p.key === plan)!;
  const creator = PLANS[1];
  const monthly = referrals * perReferral(chosen);

  const { data: coversToday } = useQuery(
    ["covers-today"],
    async () =>
      (await (await fetch("/api/stats/coversToday")).json())?.count ?? null,
    { staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false },
  );

  return (
    <div className="tw-bg-white tw-text-jam-ink">
      <NextSeo
        title="Earn with Jammable: 20% referral program"
        description="Share Jammable and earn 20% of every payment your referrals make for their first 3 months. Paid monthly in USD."
        canonical="https://www.jammable.com/earn"
      />
      <div className="tw-mx-auto tw-flex tw-w-full tw-max-w-[1180px] tw-flex-col tw-gap-6 tw-px-4 tw-pb-28 tw-pt-4 md:tw-gap-8 md:tw-pb-20 md:tw-pt-8">
        {/* Hero */}
        <section className="tw-grid tw-grid-cols-1 tw-items-center tw-gap-8 tw-overflow-hidden tw-rounded-[36px] tw-bg-jam-ink tw-p-6 tw-text-white md:tw-grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] md:tw-rounded-[48px] md:tw-p-12">
          <div className="tw-flex tw-flex-col tw-gap-5">
            <span className="tw-self-start tw-rounded-full tw-bg-jam-deep tw-px-3.5 tw-py-1.5 tw-text-xs tw-font-extrabold tw-tracking-[0.08em] tw-text-jam-soft">
              JAMMABLE AFFILIATE PROGRAM
            </span>
            <h1 className="jam-display tw-m-0 tw-text-[44px] tw-font-extrabold tw-leading-[0.98] tw-tracking-[-0.045em] md:tw-text-[68px]">
              Share Jammable.
              <br />
              <span className="tw-text-jam-lime">Earn 20%.</span>
            </h1>
            <p className="tw-m-0 tw-max-w-[480px] tw-text-[16px] tw-font-semibold tw-text-jam-soft md:tw-text-lg">
              Earn 20% of every payment your referrals make for their first{" "}
              {MONTHS} months. Paid monthly, in USD.
            </p>
            <div className="tw-flex tw-flex-wrap tw-gap-3">
              <a
                href={JOIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="tw-flex tw-h-14 tw-items-center tw-rounded-full tw-bg-jam-lime tw-px-7 tw-text-[17px] tw-font-extrabold tw-text-jam-ink tw-no-underline hover:tw-bg-[#C4EE00]"
              >
                Join the program
              </a>
              <a
                href="#how"
                className="tw-flex tw-h-14 tw-items-center tw-rounded-full tw-bg-white/10 tw-px-6 tw-text-[15px] tw-font-extrabold tw-text-white tw-no-underline hover:tw-bg-white/20"
              >
                How it works
              </a>
            </div>
          </div>

          {/* One referral, three payments, what you earn from each. */}
          <div className="tw-flex tw-flex-col tw-gap-3 tw-rounded-[30px] tw-bg-jam-deep tw-p-5 md:tw-p-6">
            <div className="tw-flex tw-items-center tw-gap-3">
              <span className="tw-flex tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-rounded-full tw-bg-jam-lime tw-text-jam-ink">
                <UserPlus size={20} strokeWidth={2.5} />
              </span>
              <div>
                <div className="tw-text-[15px] tw-font-extrabold">
                  Someone joins with your link
                </div>
                <div className="tw-text-xs tw-font-bold tw-text-jam-soft">
                  They pick Creator
                </div>
              </div>
            </div>
            {payments(creator).map((amount, i) => (
              <div
                key={i}
                className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded-[18px] tw-bg-jam-ink/60 tw-px-4 tw-py-3"
              >
                <span className="tw-text-sm tw-font-bold tw-text-jam-soft">
                  Month {i + 1} · they pay {usd(amount)}
                </span>
                <span className="jam-display tw-text-[22px] tw-font-extrabold tw-text-jam-lime">
                  +{usd(amount * RATE)}
                </span>
              </div>
            ))}
            <div className="tw-flex tw-items-center tw-justify-between tw-border-0 tw-border-t tw-border-solid tw-border-white/10 tw-px-1 tw-pt-3">
              <span className="tw-text-sm tw-font-extrabold">You earn</span>
              <span className="jam-display tw-text-[30px] tw-font-extrabold tw-leading-none">
                {usd(perReferral(creator))}
              </span>
            </div>
          </div>
        </section>

        {/* The program in four numbers */}
        <section className="tw-grid tw-grid-cols-2 tw-gap-3 md:tw-grid-cols-4">
          <Stat big="20%" small="of every payment" />
          <Stat big={`${MONTHS} months`} small="of payments per referral" />
          <Stat big="30 days" small="for them to buy after clicking" />
          <Stat big="Monthly" small="payouts, in USD" />
        </section>

        {/* How it works */}
        <section
          id="how"
          className="tw-flex tw-scroll-mt-24 tw-flex-col tw-gap-4"
        >
          <h2 className="jam-display tw-m-0 tw-text-[30px] tw-font-extrabold tw-tracking-[-0.03em] md:tw-text-[40px]">
            How it works
          </h2>
          <div className="tw-grid tw-grid-cols-1 tw-gap-3 md:tw-grid-cols-3">
            {[
              {
                Icon: UserPlus,
                title: "Join for free",
                body: "Sign up to the program and get your own referral link and dashboard.",
              },
              {
                Icon: Share2,
                title: "Share your link",
                body: "Post covers on TikTok, YouTube, Instagram or Discord with your link in the bio or description.",
              },
              {
                Icon: Wallet,
                title: "Earn from every payment",
                body: `When someone subscribes within 30 days of clicking, you get 20% of what they pay for their first ${MONTHS} months.`,
              },
            ].map(({ Icon, title, body }, i) => (
              <div
                key={title}
                className="tw-relative tw-flex tw-flex-col tw-gap-3 tw-rounded-[30px] tw-border-2 tw-border-solid tw-border-jam-line tw-p-6"
              >
                <span className="tw-absolute tw-right-6 tw-top-5 jam-display tw-text-[44px] tw-font-extrabold tw-leading-none tw-text-jam-line">
                  {i + 1}
                </span>
                <span className="tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded-full tw-bg-jam-purple tw-text-white">
                  <Icon size={22} strokeWidth={2.4} />
                </span>
                <div className="jam-display tw-text-[22px] tw-font-extrabold">
                  {title}
                </div>
                <p className="tw-m-0 tw-text-[15px] tw-font-semibold tw-text-jam-muted">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Calculator */}
        <section className="tw-grid tw-grid-cols-1 tw-gap-6 tw-rounded-[36px] tw-bg-jam-lav tw-p-6 md:tw-grid-cols-2 md:tw-rounded-[44px] md:tw-p-10">
          <div className="tw-flex tw-flex-col tw-gap-6">
            <h2 className="jam-display tw-m-0 tw-text-[30px] tw-font-extrabold tw-tracking-[-0.03em] md:tw-text-[40px]">
              What could you earn?
            </h2>
            <div className="tw-flex tw-flex-col tw-gap-3">
              <div className="tw-flex tw-items-baseline tw-justify-between">
                <span className="tw-text-[15px] tw-font-extrabold">
                  New subscribers a month
                </span>
                <span className="jam-display tw-text-[28px] tw-font-extrabold tw-text-jam-purple">
                  {referrals}
                </span>
              </div>
              <Slider
                min={1}
                max={200}
                value={referrals}
                onChange={setReferrals}
                label="New subscribers a month"
              />
            </div>
            <div className="tw-flex tw-flex-col tw-gap-3">
              <span className="tw-text-[15px] tw-font-extrabold">
                The plan they pick
              </span>
              <div className="tw-grid tw-grid-cols-3 tw-gap-2">
                {PLANS.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    aria-pressed={plan === p.key}
                    onClick={() => setPlan(p.key)}
                    className={`tw-flex tw-cursor-pointer tw-flex-col tw-items-center tw-rounded-[20px] tw-border-0 tw-px-2 tw-py-3 ${plan === p.key ? "tw-bg-jam-ink tw-text-white" : "tw-bg-white tw-text-jam-ink"}`}
                  >
                    <span className="tw-text-[15px] tw-font-extrabold">
                      {p.label}
                    </span>
                    <span
                      className={`tw-text-xs tw-font-bold ${plan === p.key ? "tw-text-jam-soft" : "tw-text-jam-muted"}`}
                    >
                      {usd(perReferral(p))} each
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="tw-flex tw-flex-col tw-justify-center tw-gap-4 tw-rounded-[30px] tw-bg-jam-ink tw-p-6 tw-text-white md:tw-p-8">
            <span className="tw-text-sm tw-font-extrabold tw-tracking-[0.06em] tw-text-jam-soft">
              ONCE YOU&apos;RE STEADY
            </span>
            <div className="tw-flex tw-items-baseline tw-gap-2">
              <span className="jam-display tw-text-[64px] tw-font-extrabold tw-leading-none tw-tracking-[-0.05em] tw-text-jam-lime md:tw-text-[80px]">
                {usdRound(monthly)}
              </span>
              <span className="tw-text-lg tw-font-bold tw-text-jam-soft">
                a month
              </span>
            </div>
            <div className="tw-text-[15px] tw-font-bold">
              That&apos;s about {usdRound(monthly * 12)} a year.
            </div>
            <p className="tw-m-0 tw-text-xs tw-font-semibold tw-text-jam-soft">
              An estimate at today&apos;s USD prices ({chosen.label}:{" "}
              {usd(chosen.first)} first month
              {chosen.first !== chosen.then
                ? `, then ${usd(chosen.then)}`
                : " each month"}
              ), assuming each referral stays for {MONTHS} months. Refunds and
              cancellations lower it.
            </p>
          </div>
        </section>

        {/* Why it's an easy share */}
        <section className="tw-grid tw-grid-cols-1 tw-gap-3 md:tw-grid-cols-3">
          <div className="tw-flex tw-items-center tw-gap-4 tw-rounded-[28px] tw-border-2 tw-border-solid tw-border-jam-line tw-p-5">
            <span className="tw-flex tw-h-12 tw-w-12 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-jam-lav tw-text-jam-purple">
              <Mic size={22} strokeWidth={2.4} />
            </span>
            <div>
              <div className="jam-display tw-text-[24px] tw-font-extrabold tw-leading-none">
                {voices ? voices.toLocaleString("en-US") : "93,000+"}
              </div>
              <div className="tw-text-[13px] tw-font-bold tw-text-jam-muted">
                voices to make covers with
              </div>
            </div>
          </div>
          <div className="tw-flex tw-items-center tw-gap-4 tw-rounded-[28px] tw-border-2 tw-border-solid tw-border-jam-line tw-p-5">
            <span className="tw-relative tw-flex tw-h-12 tw-w-12 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-[#E9FBEF]">
              <span className="tw-absolute tw-h-3 tw-w-3 tw-animate-ping tw-rounded-full tw-bg-[#22C55E] tw-opacity-60" />
              <span className="tw-h-3 tw-w-3 tw-rounded-full tw-bg-[#22C55E]" />
            </span>
            <div>
              <div className="jam-display tw-text-[24px] tw-font-extrabold tw-leading-none">
                {coversToday ? coversToday.toLocaleString("en-US") : "…"}
              </div>
              <div className="tw-text-[13px] tw-font-bold tw-text-jam-muted">
                covers made today
              </div>
            </div>
          </div>
          <div className="tw-flex tw-items-center tw-gap-4 tw-rounded-[28px] tw-border-2 tw-border-solid tw-border-jam-line tw-p-5">
            <span className="tw-flex tw-h-12 tw-w-12 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-jam-lime tw-text-jam-ink">
              <Wallet size={22} strokeWidth={2.4} />
            </span>
            <div>
              <div className="jam-display tw-text-[24px] tw-font-extrabold tw-leading-none">
                {usd(PLANS[0].first)}
              </div>
              <div className="tw-text-[13px] tw-font-bold tw-text-jam-muted">
                to start, an easy yes for your audience
              </div>
            </div>
          </div>
        </section>

        {/* The rules, from the terms */}
        <section className="tw-flex tw-flex-col tw-gap-4">
          <h2 className="jam-display tw-m-0 tw-text-[30px] tw-font-extrabold tw-tracking-[-0.03em] md:tw-text-[40px]">
            Keep it clean
          </h2>
          <div className="tw-grid tw-grid-cols-1 tw-gap-3 md:tw-grid-cols-2">
            <div className="tw-flex tw-flex-col tw-gap-3 tw-rounded-[30px] tw-bg-jam-lav tw-p-6">
              <div className="tw-text-[15px] tw-font-extrabold">Go for it</div>
              {[
                "Post covers and videos with your link",
                "Share on TikTok, YouTube, Instagram and Discord",
                "Tell people honestly what Jammable does",
              ].map((t) => (
                <div
                  key={t}
                  className="tw-flex tw-items-center tw-gap-3 tw-text-[15px] tw-font-semibold"
                >
                  <span className="tw-flex tw-h-6 tw-w-6 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-jam-lime tw-text-jam-ink">
                    <Check size={14} strokeWidth={3.5} />
                  </span>
                  {t}
                </div>
              ))}
            </div>
            <div className="tw-flex tw-flex-col tw-gap-3 tw-rounded-[30px] tw-border-2 tw-border-solid tw-border-jam-line tw-p-6">
              <div className="tw-text-[15px] tw-font-extrabold">
                Not allowed
              </div>
              {[
                "Google Search ads, or any ads sending traffic to Jammable",
                "Spam or unsolicited messages",
                "False or misleading claims about Jammable",
              ].map((t) => (
                <div
                  key={t}
                  className="tw-flex tw-items-center tw-gap-3 tw-text-[15px] tw-font-semibold"
                >
                  <span className="tw-flex tw-h-6 tw-w-6 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-jam-ink tw-text-white">
                    <X size={14} strokeWidth={3.5} />
                  </span>
                  {t}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="tw-flex tw-flex-col tw-gap-4">
          <h2 className="jam-display tw-m-0 tw-text-[30px] tw-font-extrabold tw-tracking-[-0.03em] md:tw-text-[40px]">
            Questions
          </h2>
          <Accordion
            type="single"
            collapsible
            className="tw-w-full tw-rounded-[28px] tw-border-2 tw-border-solid tw-border-jam-line tw-px-5 tw-py-2"
          >
            {[
              [
                "How much do I earn?",
                `20% of every payment a customer you referred makes, for their first ${MONTHS} months. One Creator referral earns you ${usd(perReferral(creator))}.`,
              ],
              [
                "When do I get paid?",
                "Once a month, for the commissions you earned the month before. All payouts are in USD.",
              ],
              [
                "How long does a referral count?",
                "They need to buy within 30 days of clicking your link.",
              ],
              [
                "Do renewals count?",
                `Yes, for the customer's first ${MONTHS} months of payments.`,
              ],
              [
                "Who can join?",
                "Anyone aged 13 or over who follows the program terms.",
              ],
              [
                "Where do I see my earnings?",
                "In your affiliate dashboard, where you also get your link.",
              ],
            ].map(([q, a], i) => (
              <AccordionItem key={q} value={`q${i}`}>
                <AccordionTrigger className="tw-text-left tw-font-extrabold">
                  {q}
                </AccordionTrigger>
                <AccordionContent className="tw-font-semibold tw-text-jam-muted">
                  {a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <p className="tw-m-0 tw-text-[13px] tw-font-semibold tw-text-jam-muted">
            The full rules are in the{" "}
            <Link
              href="/affiliate/terms"
              className="tw-font-extrabold tw-text-jam-purple"
            >
              affiliate terms
            </Link>
            . Questions: affiliates@jammable.com
          </p>
        </section>

        {/* Last call */}
        <section className="tw-flex tw-flex-col tw-items-center tw-gap-4 tw-rounded-[36px] tw-bg-jam-ink tw-px-6 tw-py-10 tw-text-center tw-text-white md:tw-rounded-[48px] md:tw-py-14">
          <div className="jam-display tw-text-[34px] tw-font-extrabold tw-leading-none tw-tracking-[-0.04em] md:tw-text-[48px]">
            Your audience already loves covers
          </div>
          <p className="tw-m-0 tw-text-[15px] tw-font-semibold tw-text-jam-soft">
            Get paid when they make their own.
          </p>
          <a
            href={JOIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="tw-mt-2 tw-flex tw-h-14 tw-items-center tw-rounded-full tw-bg-jam-lime tw-px-8 tw-text-[17px] tw-font-extrabold tw-text-jam-ink tw-no-underline hover:tw-bg-[#C4EE00]"
          >
            Join the program
          </a>
        </section>
      </div>
    </div>
  );
}

export const getServerSideProps: GetServerSideProps = async ({ res }) => {
  res.setHeader(
    "Cache-Control",
    "public, s-maxage=3600, stale-while-revalidate=86400",
  );
  try {
    const Model = (await import("@/models/Model")).default;
    await connectToDb();
    return { props: { voices: await Model.estimatedDocumentCount() } };
  } catch {
    return { props: { voices: 0 } };
  }
};
